import { cn } from '@core/utils';

import { Image } from '@/components/client';
import { WELLO_IMAGE_PREFIX_URL } from '@/constants';

interface RatingProps {
  value: number;
  count?: number;
  size?: number;
  className?: string;
  contentClassName?: {
    rating?: string;
    count?: string;
  };
}

export const Rating = ({
  value,
  count,
  size = 12,
  className,
  contentClassName,
}: RatingProps) => {
  const stars = (
    <div className={cn('flex items-center', contentClassName?.rating)}>
      <div className="flex gap-2">
        {Array.from({ length: 5 }, (_, index) => {
          const fillAmount = Math.max(0, Math.min(1, value - index));

          return <Star key={index} size={size} fillAmount={fillAmount} />;
        })}
      </div>
    </div>
  );

  return (
    <div className={cn('flex items-center', className)}>
      {stars}
      {count !== undefined && (
        <span
          className={cn(
            'ml-4 font-400 text-Gray-600 typo-caption2',
            contentClassName?.count,
          )}
        >
          ({count.toLocaleString()})
        </span>
      )}
    </div>
  );
};

interface StarProps {
  size: number;
  fillAmount: number;
}

const Star = ({ size, fillAmount }: StarProps) => {
  const commonStarProps = {
    loading: 'lazy',
    alt: '별',
    width: size,
    height: size,
    quality: 75,
  } as const;

  return (
    <div
      className="relative shrink-0 flex-center"
      style={{ width: size, height: size }}
    >
      {(() => {
        if (fillAmount < 0.1) {
          return (
            <Image
              src={`${WELLO_IMAGE_PREFIX_URL}/static/star/star-0.png`}
              {...commonStarProps}
            />
          );
        }

        if (fillAmount < 0.3) {
          return (
            <Image
              src={`${WELLO_IMAGE_PREFIX_URL}/static/star/star-0.25.png`}
              {...commonStarProps}
            />
          );
        }

        if (fillAmount < 0.6) {
          return (
            <Image
              src={`${WELLO_IMAGE_PREFIX_URL}/static/star/star-0.5.png`}
              {...commonStarProps}
            />
          );
        }

        if (fillAmount < 0.8) {
          return (
            <Image
              src={`${WELLO_IMAGE_PREFIX_URL}/static/star/star-0.75.png`}
              {...commonStarProps}
            />
          );
        }

        return (
          <Image
            src={`${WELLO_IMAGE_PREFIX_URL}/static/star/star-1.png`}
            {...commonStarProps}
          />
        );
      })()}
    </div>
  );
};
